import { Loader, Tooltip } from "@cloudflare/kumo";
import { ArrowClockwiseIcon, CheckIcon, PlusIcon } from "@phosphor-icons/react";
import { memo } from "react";
import { IconCopyButton } from "./IconCopyButton";
import { ScrollableCodeBlock } from "../../ScrollableCodeBlock";
import { formatDuration, formatJson } from "./workflow-format";
import { WorkflowTimestamp } from "./WorkflowTimestamp";
import type { WorkflowStepRecord } from "../../api/workflows/model.ts";

// Adapted from workers-sdk's local-explorer-ui workflow StepRow onto this
// runtime's WorkflowStepRecord (one record per execution, no attempt log).

function StepStatusIcon({ status }: { status: WorkflowStepRecord["status"] }) {
  if (status === "errored") {
    return (
      <div className="flex size-5 items-center justify-center rounded bg-kumo-badge-red">
        <span className="text-xs font-bold text-white">!</span>
      </div>
    );
  }
  if (status === "complete") {
    return (
      <div className="flex size-5 items-center justify-center rounded bg-kumo-badge-teal">
        <CheckIcon size={12} weight="bold" className="text-white" />
      </div>
    );
  }
  if (status === "pending") {
    return (
      <div className="flex size-5 items-center justify-center rounded bg-kumo-fill">
        <span className="size-2 rounded-full bg-kumo-subtle" />
      </div>
    );
  }
  return (
    <span>
      <Loader size={18} />
    </span>
  );
}

const TYPE_BADGE_STYLES: Record<string, string> = {
  do: "bg-kumo-tint text-kumo-subtle",
  sleep: "bg-kumo-overlay text-kumo-subtle",
  sleepUntil: "bg-kumo-overlay text-kumo-subtle",
  waitForEvent: "bg-kumo-badge-purple/10 text-kumo-badge-purple"
};

const TYPE_BADGE_LABELS: Record<string, string> = {
  sleep: "sleep",
  sleepUntil: "sleep until",
  waitForEvent: "wait for event"
};

export function getStepKey(step: WorkflowStepRecord): string {
  return step.token;
}

export const WorkflowStepRow = memo(function WorkflowStepRow({
  step,
  isExpanded,
  onToggleExpanded,
  onRestartFromStep,
  onOpenSource
}: {
  step: WorkflowStepRecord;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRestartFromStep?: (step: WorkflowStepRecord) => void;
  onOpenSource?: (line: number) => void;
}) {
  const hasDetails =
    step.output !== undefined || step.error !== undefined || step.kind === "do";

  return (
    <div className="border-b border-kumo-fill p-1 last:border-b-0">
      {/* Collapsed row */}
      <div
        className={`grid h-10 grid-cols-[20px_1fr_160px_160px_80px_28px_24px] items-center gap-3 rounded-lg px-2 transition-colors ${hasDetails ? "cursor-pointer hover:bg-kumo-fill" : ""}`}
        onClick={hasDetails ? onToggleExpanded : undefined}
      >
        <StepStatusIcon status={step.status} />

        <div className="flex items-center gap-2 overflow-hidden">
          {step.kind !== "do" && (
            <span
              className={`shrink-0 rounded px-1.5 py-0.5 text-sm ${TYPE_BADGE_STYLES[step.kind] ?? TYPE_BADGE_STYLES.do}`}
            >
              {TYPE_BADGE_LABELS[step.kind] ?? step.kind}
            </span>
          )}
          <span className="truncate text-sm text-kumo-default">
            {step.name}
          </span>
          {step.count > 1 && (
            <span className="shrink-0 font-mono text-xs text-kumo-subtle">
              #{step.count}
            </span>
          )}
        </div>

        <WorkflowTimestamp value={step.startedAt} />
        <WorkflowTimestamp value={step.completedAt} />
        <span className="text-sm text-kumo-subtle">
          {formatDuration(step.startedAt, step.completedAt)}
        </span>

        <div className="flex items-center justify-center">
          {onRestartFromStep && (
            <Tooltip content="Restart from this step" asChild>
              <button
                aria-label="Restart from this step"
                className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border border-kumo-fill bg-kumo-base text-kumo-default transition-colors hover:bg-kumo-fill disabled:cursor-not-allowed disabled:opacity-40"
                onClick={(event) => {
                  event.stopPropagation();
                  onRestartFromStep(step);
                }}
                type="button"
              >
                <ArrowClockwiseIcon size={14} />
              </button>
            </Tooltip>
          )}
        </div>

        <div className="flex items-center justify-center">
          {hasDetails ? (
            <PlusIcon
              size={14}
              className={`text-kumo-subtle transition-transform ${isExpanded ? "rotate-45" : ""}`}
            />
          ) : (
            <div className="w-6" />
          )}
        </div>
      </div>

      {/* Expanded detail panel */}
      {isExpanded && hasDetails && (
        <div className="-mx-1 -mb-1">
          <div className="mt-1 h-2 rounded-t-lg border-t border-kumo-fill" />
          <div className="px-4 pt-3 pb-4">
            <StepDetails step={step} onOpenSource={onOpenSource} />
          </div>
        </div>
      )}
    </div>
  );
});

function StepCodeCard({ label, content }: { label: string; content: string }) {
  return (
    <div>
      <h5 className="mb-2 text-sm font-medium text-kumo-default">{label}</h5>
      <div className="relative overflow-hidden rounded-lg border border-kumo-fill bg-kumo-base">
        <ScrollableCodeBlock content={content} />
        <div className="absolute top-1.5 right-1.5">
          <IconCopyButton text={content} label={`Copy ${label.toLowerCase()}`} />
        </div>
      </div>
    </div>
  );
}

function StepDetails({
  step,
  onOpenSource
}: {
  step: WorkflowStepRecord;
  onOpenSource?: (line: number) => void;
}) {
  const errorText = step.error
    ? `${step.error.name}: ${step.error.message}`
    : null;
  const outputText =
    step.status === "complete" && step.output !== undefined
      ? formatJson(step.output)
      : null;

  const leftLabel = errorText ? "Error" : "Output";
  const leftContent = errorText ?? outputText ?? "N/A";

  return (
    <div className="space-y-3">
      <StepCodeCard label={leftLabel} content={leftContent} />
      {onOpenSource && step.line > 0 && (
        <button
          type="button"
          className="cursor-pointer rounded text-xs text-kumo-link hover:underline"
          onClick={() => onOpenSource(step.line)}
        >
          Open source at line {step.line}
        </button>
      )}
    </div>
  );
}
